import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ExternalLink, Eye, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export default function Shop() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [products, setProducts] = useState<any[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFavorites, setShowFavorites] = useState(false);
  const { user } = useAuth();
  const itemsPerPage = 12;

  useEffect(() => {
    fetchProducts();
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const fetchProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('shop_items')
      .select('*')
      .is('pta_id', null)
      .eq('status', 'active')
      .eq('approval_status', 'approved')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error loading products",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setProducts(data || []);
    }
    setLoading(false);
  };

  const fetchFavorites = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('user_shop_favorites')
      .select('shop_item_id')
      .eq('user_id', user.id);
    
    setFavorites(data?.map(f => f.shop_item_id) || []);
  };

  const toggleFavorite = async (productId: string) => {
    if (!user) {
      toast({
        title: "Login required",
        description: "Please login to save favorites",
        variant: "destructive"
      });
      return;
    }

    const isFavorite = favorites.includes(productId);
    
    if (isFavorite) {
      const { error } = await supabase
        .from('user_shop_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('shop_item_id', productId);
      
      if (!error) {
        setFavorites(favorites.filter(id => id !== productId));
        toast({ title: "Removed from favorites" });
      }
    } else {
      const { error } = await supabase
        .from('user_shop_favorites')
        .insert({ user_id: user.id, shop_item_id: productId });
      
      if (!error) {
        setFavorites([...favorites, productId]);
        toast({ title: "Added to favorites" });
      }
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || product.category.toLowerCase() === categoryFilter.toLowerCase();
    const matchesFavorites = !showFavorites || favorites.includes(product.id);
    return matchesSearch && matchesCategory && matchesFavorites;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">PTA Shop</h1>
        <p className="text-muted-foreground mt-2">
          Browse and purchase PTA-related items through our Amazon affiliate
        </p>
      </div>

      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Affiliate Disclosure:</strong> PTA Hub participates in the Amazon Associates Program. This means we may earn a small commission when you make purchases through links on this page, at no additional cost to you. These commissions help support and maintain our platform.
        </p>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Shop Items</CardTitle>
          <CardDescription>Essential supplies and equipment for your PTA</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="events">Events</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="catering">Catering</SelectItem>
                  <SelectItem value="activities">Activities</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                </SelectContent>
              </Select>
              {user && (
                <Button
                  variant={showFavorites ? "default" : "outline"}
                  onClick={() => setShowFavorites(!showFavorites)}
                >
                  <Heart className={`h-4 w-4 mr-2 ${showFavorites ? "fill-current" : ""}`} />
                  Favorites {showFavorites && `(${favorites.length})`}
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No products available yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {displayedProducts.map((product) => (
                  <Dialog key={product.id}>
                    <DialogTrigger asChild>
                      <Card className="shadow-card hover:shadow-elevated transition-shadow cursor-pointer">
                        <CardContent className="pt-6 flex flex-col h-full">
                          <div className="text-center mb-4 flex-1">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} className="w-full h-64 object-contain rounded-lg mb-3" />
                            ) : (
                              <div className="text-6xl mb-3">📦</div>
                            )}
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-lg flex-1 text-left">{product.name}</h4>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavorite(product.id);
                                }}
                                className="h-8 w-8"
                              >
                                <Heart
                                  className={`h-4 w-4 ${
                                    favorites.includes(product.id)
                                      ? "fill-red-500 text-red-500"
                                      : "text-muted-foreground"
                                  }`}
                                />
                              </Button>
                            </div>
                            <p className="text-sm text-muted-foreground text-left">{product.category}</p>
                          </div>
                          <div className="flex justify-center mt-auto">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(product.amazon_url, '_blank');
                              }}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View on Amazon
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{product.name}</DialogTitle>
                        <DialogDescription>Product details and information</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="text-center">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} className="w-full h-80 object-contain rounded-lg" />
                          ) : (
                            <div className="text-8xl mb-4">📦</div>
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Description</h4>
                          <p className="text-sm text-muted-foreground">
                            {product.description || `High-quality ${product.name.toLowerCase()} perfect for PTA events and activities.`}
                          </p>
                        </div>
                        <Button
                          className="w-full"
                          onClick={() => window.open(product.amazon_url, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View on Amazon
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredProducts.length)} of {filteredProducts.length} items
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
